import { useContext, useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { UserProgramContext } from "../../context/user-program/user-program";
import { ROUTE_HOME, SUBROUTE_HOME_SECTION } from "../../constants";
import { Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { MenuDrawer } from "./menu-drawer";
import LogoutIcon from "@mui/icons-material/Logout";
import { useSignOut } from "../../hooks/useSignOut";
import { ContainedButton } from "../styled/contained-button";
import { VideoGallery } from "../video-gallery/video-gallery";
import { VIDEO_DATA } from "../../data/video-data";

export const Section = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { title } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { program } = useContext(UserProgramContext);

  const { signOut } = useSignOut();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  if (!program) {
    return null;
  }

  const section = program.sections.find((section) => section.title === title);

  const selectedTab = location.pathname.slice(
    location.pathname.lastIndexOf("/") + 1
  );

  if (!section || !title) {
    navigate(ROUTE_HOME);
    return null;
  }

  const handleTabChange = (
    newTab: "videos" | "documents" | "monthly-training"
  ) => {
    navigate(
      `${ROUTE_HOME}${SUBROUTE_HOME_SECTION}${newTab}`.replace(":title", title)
    );
  };

  const tabLabels = new Map([
    ["videos", "Videos"],
    ["documents", "Dokumente"],
    ["monthly-training", "Monatliche Schulung"],
  ]);

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "black",
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <Grid
            container
            flexWrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item sx={{ display: { sm: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => setDrawerOpen(!drawerOpen)}
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid item maxWidth="40dvw">
              <Typography variant="h6" noWrap component="div">
                {section.title} /{" "}
                {selectedTab && (
                  <Typography>{tabLabels.get(selectedTab)}</Typography>
                )}
              </Typography>
            </Grid>

            <Grid item>
              <ContainedButton
                onClick={signOut}
                size={isMobile ? "small" : "medium"}
                startIcon={<LogoutIcon />}
              >
                Abmelden
              </ContainedButton>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: "240px" }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <MenuDrawer
          section={section}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          isMobile={isMobile}
          setSelectedTab={handleTabChange}
        />
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: "calc(100% - 240px)" },
        }}
      >
        <Toolbar />
        {/* Verhalten section content */}
        {title === "Verhaltenstraining" && (
          <Box>
            <Typography paragraph>
              In den Ernährungsschulungen und im psychologischen Gespräch
              (Live-Veranstaltungen) erhalten Sie viele Informationen zu
              Verhaltensänderungen – praxisnah, alltagstauglich und auf Ihre
              OP-Vorbereitung abgestimmt.
            </Typography>
            <Typography paragraph>
              Wichtig: Diagnostik, individuelle Empfehlungen und Behandlung
              finden ausschließlich in Ihren Live-Veranstaltungen statt.
            </Typography>
            <Typography paragraph>
              Wenn Sie deutliche Warnzeichen (z. B. häufige Essanfälle, stark
              gedrückte Stimmung über Wochen) bemerken, sprechen Sie uns bitte
              an – wir planen dann die nächsten Schritte gemeinsam.
            </Typography>
            <Typography paragraph>
              Ergänzend können Sie unsere Videomediathek nutzen.
            </Typography>
          </Box>
        )}
        {/* Temporarily disabled until new content is ready */}
        {selectedTab === "videos" && (
          <>
            <Typography paragraph>
              <strong>Hinweis Videomediathek</strong>
              <br />
              <br />
              Unsere Videomediathek ist ein freiwilliges Zusatzangebot.
              Maßgeblich für den erfolgreichen Abschluss des MMK-Programms sind
              ausschließlich Ihre monatliche Teilnahme an den
              Ernährungs-Schulungen und dem psychologischen Gespräch
              (Live-Veranstaltungen) sowie die fristgerechte Abgabe der
              geforderten Protokolle und Nachweise. Die Videos ersetzen diese
              Pflichten nicht, werden nicht als Anwesenheit angerechnet und sind
              kein Online- Lehrgang (keine Kursstruktur, keine Prüfung/Test,
              kein Zertifikat).
              <br />
              <br />
              Sie können die Inhalte in beliebiger Reihenfolge und in Ihrem
              eigenen Tempo nutzen. Individuelle Empfehlungen/Anpassungen (z. B.
              bei Unverträglichkeiten oder Medikation) erfolgen ausschließlich
              in den Ernährungs-Schulungen und dem psychologischen Gespräch
              (Live-Veranstaltungen). Rückfragen sind ausschließlich zu den
              Inhalten der Live-Veranstaltungen vorgesehen. Die Videos dienen
              lediglich der allgemeinen Information.
            </Typography>

            {/* Show video gallery for specific sections */}
            {title && VIDEO_DATA[title] && VIDEO_DATA[title].length > 0 && (
              <VideoGallery videos={VIDEO_DATA[title]} sectionTitle={title} />
            )}
          </>
        )}

        {/* Sport section content */}
        {title === "Sport" && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
              Sportprotokoll
            </Typography>
            <Typography paragraph>
              Vor einer Magen-OP verlangt der Medizinische Dienst (MD, früher
              MDK) einen Nachweis regelmäßiger Bewegung als Teil der
              konservativen Therapie (MMK).
            </Typography>
            <Typography paragraph>
              Ziel ist, dass Sie über mehrere Monate konsequent aktiv sind, also
              regelmäßig Sport treiben – ob allein, in einer Gruppe oder in
              einem Verein. Dies ist Grundlage für einen erfolgreichen
              OP-Verlauf und für die Kostenübernahme.
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Sportprotokoll
            </Typography>

            <Typography paragraph>
              Sie führen über Ihre sportliche Aktivität ein selbstgeführtes
              Sportprotokoll.
            </Typography>
            <Typography paragraph>
              &rarr; Laden Sie dazu unsere Vorlage herunter oder nutzen Sie die
              Ihres Adipositaszentrums.
            </Typography>
            <Typography paragraph>
              Wir prüfen das Protokoll auf Vollständigkeit und Plausibilität und
              nehmen es in Ihren MMK-Endbefund auf.
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Anforderungen
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" paragraph>
                Kurz, vollständig und nachvollziehbar
              </Typography>
              <Typography component="li" paragraph>
                <strong>Dauer:</strong> Über die gesamte Therapielaufzeit
                <br />
                (z. B. bei 6 Monaten Programm → 6 Monate Protokoll; bei 3
                Monaten → 3 Monate)
              </Typography>
              <Typography component="li" paragraph>
                <strong>Richtwert:</strong> ca. 150 Minuten pro Woche, verteilt
                auf 2–5 Einheiten
              </Typography>
              <Typography component="li" paragraph>
                <strong>Eintragung:</strong>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <Typography component="li" paragraph>
                    Wöchentlich Datum, Art der Aktivität und Dauer (in Minuten)
                  </Typography>
                  <Typography component="li" paragraph>
                    Ausfälle (z. B. Krankheit): im Protokoll vermerken („krank“)
                  </Typography>
                  <Typography component="li" paragraph>
                    → keine „leeren“ Wochen; ggf. ärztliches Attest (optional)
                  </Typography>
                </Box>
              </Typography>
              <Typography component="li" paragraph>
                <strong>Abschluss:</strong>
                <br />
                Am Ende vom Zeugen unterschreiben lassen
                <br />
                (Partnerin, Familienmitglied, Freundin, Nachbar*in etc.)
              </Typography>
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Was zählt als „Sport“?
            </Typography>

            <Typography paragraph>
              Anerkannt wird gezielte körperliche Aktivität mit
              Trainingscharakter, die Ihre Ausdauer oder Kraft steigert –
              gelenkschonend beginnen und langsam steigern:
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" paragraph>
                Walking (zügig), Nordic Walking
              </Typography>
              <Typography component="li" paragraph>
                Schwimmen, Aquafitness, Aquajogging, Wassergymnastik
              </Typography>
              <Typography component="li" paragraph>
                Radfahren / Ergometer, Crosstrainer, moderates Laufband
              </Typography>
              <Typography component="li" paragraph>
                Fitnessstudio: Ausdauertraining + moderates Krafttraining (z. B.
                Gerätezirkel)
              </Typography>
              <Typography component="li" paragraph>
                Kurse, Rehasport oder Physiotherapie mit Bewegungsschwerpunkt
              </Typography>
              <Typography component="li" paragraph>
                Eigenübungen zu Hause (App, Video, Heimtrainer) → gut
                dokumentieren
              </Typography>
            </Box>

            <Typography
              paragraph
              sx={{
                fontStyle: "italic",
                backgroundColor: "#f5f5f5",
                p: 2,
                borderRadius: 1,
              }}
            >
              <strong>Praxis-Tipp:</strong> Planen Sie feste Termine (z. B. Mo /
              Mi / Sa), damit Sie jede Woche sicher ca. 150 Minuten erreichen.
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Was zählt nicht als Sport?
            </Typography>

            <Typography paragraph>
              Nicht anerkannt werden Alltagsbewegungen ohne Trainingsziel oder
              passive Maßnahmen.
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" paragraph>
                Bummeln, Einkaufen, Hausarbeit, berufliches Stehen oder Gehen
              </Typography>
              <Typography component="li" paragraph>
                Normale Gassirunde mit dem Hund
              </Typography>
              <Typography component="li" paragraph>
                Massage, Sauna, Wellness
              </Typography>
              <Typography component="li" paragraph>
                Einzelaktionen ohne Plan oder Dokumentation
              </Typography>
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Optionale Zusatznachweise
            </Typography>

            <Typography paragraph>
              Diese sind nicht verpflichtend, können aber bei manchen
              Kostenträgern die Akzeptanz erhöhen. Nutzen Sie möglichst Orte mit
              verlässlicher Anwesenheitsdokumentation:
            </Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" paragraph>
                Fitnessstudio: Anwesenheitslisten, Ausdrucke, Trainer-Stempel
              </Typography>
              <Typography component="li" paragraph>
                Sportverein oder Kursleitung: z. B. Wassergymnastik,
                Walking-Gruppe, VHS-Kurse
              </Typography>
              <Typography component="li" paragraph>
                Physiotherapie/Rehasport: Teilnahme in geleiteten Gruppen
              </Typography>
              <Typography component="li" paragraph>
                Schwimmbad/Kurse: Teilnahmelisten
              </Typography>
              <Typography component="li" paragraph>
                Ärztin/Arzt oder Therapeut:in: betreutes Bewegungsprogramm
              </Typography>
              <Typography component="li" paragraph>
                Selbsthilfegruppe (SHG): mit Bewegungsangeboten
              </Typography>
              <Typography component="li" paragraph>
                Apps/Tracker: als ergänzender Nachweis
              </Typography>
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Abschluss
            </Typography>

            <Typography paragraph>Am Ende Ihrer Therapielaufzeit:</Typography>

            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <Typography component="li" paragraph>
                Sportprotokoll hochladen
              </Typography>
              <Typography component="li" paragraph>
                Wir vermerken das Sportprotokoll im MMK-Endbericht
              </Typography>
              <Typography component="li" paragraph>
                Den Endbericht reichen Sie bei Ihrem Adipositaszentrum oder
                Ihrer Klinik ein.
              </Typography>
            </Box>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Download
            </Typography>

            <Typography paragraph>Überschrift: Download</Typography>
            <Typography paragraph>
              Klicken Sie auf „Vorlage Sportprotokoll herunterladen“ und laden
              Sie die Vorlage herunter.
            </Typography>
            <Typography paragraph>
              Drucken Sie die Vorlage aus oder füllen Sie diese digital aus.
              Schreiben Sie auf, wann und welchen Sport Sie machen. Füllen Sie
              das Dokument vollständig aus. Lassen Sie Ihre/n Partner/in, einen
              Verwandten oder einen Freund unterschreiben.
            </Typography>
            <Typography paragraph>
              Machen Sie von dem vollständig ausgefüllten Protokoll ein Foto
              oder einen Scan und laden Sie es am Ende Ihrer Therapielaufzeit
              hier hoch.
            </Typography>
          </Box>
        )}

        <Outlet />
      </Box>
    </Box>
  );
};
